import {
  Button,
  Box,
  Container,
  CircularProgress,
  IconButton,
  Switch,
} from "@mui/material";
import { Typography, TextField, Note, Tooltip } from "@appsflyer/fe-ui-core";
import transition from "../components/transition";
import { useState, useEffect } from "react";
import { translateCode } from "../../services/api";
import { useLocation, useNavigate } from "react-router-dom";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import {
  IProgress,
  ISessionProps,
  SessionStatus,
} from "../components/IProgress";
import { t } from "i18next";
import { EvgenEvent, EvgenEventsObject } from "../../inapp/IEvgen";
import { amplitudeTrack_migrateClick } from "../../services/utils";
import { DJSimpleTextBlock } from "../components/codeblock";

const SdkMigration = () => {
  const [isConverted, setIsConverted] = useState(false);
  const [inputCode, setInputCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [viewMode, setViewMode] = useState<"single" | "split">("single");
  const [migrateClick, setMigrateClick] = useState(0);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      console.log("Copied to clipboard:", text);
    });
  };

  const navigate = useNavigate();
  const location = useLocation();

  const migrationRouteType =
    sessionStorage.getItem("migrationRouteType") || "startsdk";

  const migrateCode = () => {
    setMigrateClick(migrateClick + 1);
    setIsConverted(true);
    setInputCode(inputCode);
    setIsLoading(true);

    let dk = "";
    const lsDevKey = localStorage.getItem("devKey");
    if (lsDevKey) {
      dk = JSON.parse(lsDevKey);
    }

    translateCode(inputCode, migrationRouteType)
      ?.then((res) => {
        console.log("res", res);
        if (!res.newCode) {
          console.warn("newCode is missing in response", res);
          return;
        }

        const getBody = res;
        console.log("res", getBody);

        setIsLoading(false);
        setNewCode(getBody.newCode.replace(/YOUR_APPSFLYER_DEV_KEY/g, `"${dk}"`));
      })
      .catch((err) => {
        setIsLoading(false);
        console.log("err", err);
        console.error(err);
      });
    console.log(newCode);

    // Increment click count and track the event in Amplitude
    amplitudeTrack_migrateClick(location.state, migrateClick); // Amplitude tracking
    sendMigrateClicksToBackend(); // Send to backend
  };

  const sendMigrateClicksToBackend = async () => {
    const newState: ISessionProps = {
      ...location.state,
      migrateClick: migrateClick,
    };
    navigate(location.pathname, {
      replace: true,
      state: newState,
    });
  };

  useEffect(() => {
    console.log("Updated newCode:", newCode);
  }, [newCode]);

  const retry = () => {
    setIsConverted(false);
    setNewCode("");
    setInputCode("");
    sessionStorage.removeItem("sdkMigrationState"); // Clear saved state
  };

  const proceedToTest = () => {
    if (!isConverted || !newCode) return;

    // Save the current state in sessionStorage before navigation
    sessionStorage.setItem(
      "sdkMigrationState",
      JSON.stringify({ isConverted, inputCode, newCode })
    );

    const progressArr: IProgress[] = location.state?.progress.slice();

    progressArr.push({
      id: "inapp_testevents",
      text: "progress_getres",
      order: progressArr.length + 1,
      startTime: new Date(),
    });

    // Default progressState without evgenEventsObject
    let progressState: ISessionProps = {
      ...location.state,
      progress: progressArr,
      currentPage: location.state?.currentPage + 1,
      sessionStatus: SessionStatus.ImplementedNotTested,
    };

    // Add evgenEventsObject only if migrationRouteType is "inapp"
    if (migrationRouteType === "inapp") {
      const evgenEventsObject: EvgenEventsObject =
        generateEvgenEventsFromCode(newCode);
      progressState = {
        ...progressState,
        evgenEventsObject,
        isEvgenTesting: true,
      };
    }

    sessionStorage.setItem("evgenState", JSON.stringify(progressState));

    if (migrationRouteType == "deeplink") {
      navigate("/migration/deeplink/android/deeplinktype", {
        state: progressState,
      });
    } else {
      navigate(
        `/migration/${migrationRouteType}/${location.state.os}/testtype`,
        {
          state: progressState,
        }
      );
    }
  };

  const generateEvgenEventsFromCode = (code: string): EvgenEventsObject => {
    // console.log("DEBUG: Parsing newCode:\n", code);

    const eventRegex =
      /logEvent\s*\(\s*.*?,\s*(AFInAppEventType\.[A-Z_]+|".+?")\s*,\s*(\w+)\s*\)/g;

    const paramRegex =
      /(\w+)\.put\(\s*(AFInAppEventParameterName\.[A-Z_]+|".+?")\s*,\s*([^;]+?)\s*\);/g;

    const events: EvgenEvent[] = [];
    const eventValuesMap = new Map(); // Store eventValues variable names and parameters

    // Helper function to format identifiers
    const formatIdentifier = (identifier) => `af_${identifier.toLowerCase()}`;
    const formatName = (name) =>
      name
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());

    // Extract event parameters first
    let paramMatch;
    while ((paramMatch = paramRegex.exec(code)) !== null) {
      const eventValuesVar = paramMatch[1]; // e.g., eventValues1
      const parameterIdentifierRaw = paramMatch[2]
        .replace(/AFInAppEventParameterName\./, "")
        .replace(/"/g, "");
      const parameterValue = paramMatch[3].trim().replace(/"/g, ""); // Remove quotes

      const parameterIdentifier = formatIdentifier(parameterIdentifierRaw);
      const parameterName = formatName(parameterIdentifierRaw);

      console.log(
        `DEBUG: Found parameter for ${eventValuesVar}: ${parameterIdentifier} = ${parameterValue}`
      );

      if (!eventValuesMap.has(eventValuesVar)) {
        eventValuesMap.set(eventValuesVar, []);
      }
      eventValuesMap.get(eventValuesVar).push({
        parameterType: "generated",
        parameterIdentifier,
        parameterName,
        parameterDescription: `Extracted from migrated SDK`,
        parameterValueType:
          typeof parameterValue === "number" ? "number" : "string",
        parameterValueExample: parameterValue,
      });
    }

    // Extract events
    let match;
    while ((match = eventRegex.exec(code)) !== null) {
      const eventIdentifierRaw = match[1]
        .replace(/AFInAppEventType\./, "")
        .replace(/"/g, "");
      const eventValuesVar = match[2]; // The eventValues variable associated with this event

      const eventIdentifier = formatIdentifier(eventIdentifierRaw);
      const eventName = formatName(eventIdentifierRaw);

      const parameters = eventValuesMap.get(eventValuesVar) || [];

      events.push({
        eventType: "generated",
        eventIdentifier,
        eventName,
        eventPurposeDescription: "Generated from SDK migration",
        eventTriggerDescription: "Triggered in in-app flow",
        parameters,
        selectedEventType: "predefined",
      });
    }

    return {
      eventSetName: "Migrated In-App Events",
      createdAt: Date.now(),
      lastModified: Date.now(),
      events,
    };
  };

  useEffect(() => {
    // Try to retrieve the state from sessionStorage
    const savedState = sessionStorage.getItem("sdkMigrationState");

    if (savedState) {
      const { isConverted, inputCode, newCode } = JSON.parse(savedState);

      setIsConverted(isConverted);
      setInputCode(inputCode);
      setNewCode(newCode);

      console.log("Restored from sessionStorage:", {
        isConverted,
        inputCode,
        newCode,
      });
    }
  }, []);

  return (
    <Container maxWidth={"lg"}>
      <Box
        padding={3}
        display={"flex"}
        flexDirection={"column"}
        style={{ minHeight: "20vh" }}
      >
        <Typography variant="h1">
          {`Branch SDK to AppsFlyer SDK migration: ${
            migrationRouteType === "startsdk"
              ? "Start SDK"
              : migrationRouteType === "inapp"
                ? "In-App Events"
                : "Deep Linking"
          }`}
        </Typography>

        <Box display={"flex"} sx={{ mt: 2 }} gap={1} flexDirection={"column"}>
          <>
            {isLoading && (
              <Box mt={20} mb={20} mx="auto" width="fit-content">
                <CircularProgress />
              </Box>
            )}
          </>
          {!isConverted && !isLoading && (
            <Box
              display={"flex"}
              flexDirection={"column"}
              // mx={40}
              width={"100%"}
              gap={2}
              mb={3}
              alignItems={"flex-start"}
            >
              {migrationRouteType === "inapp" ? (
                <>
                  <Note variant="tip" title="" sx={{ width: "600px" }}>
                    Paste here the complete code of the Branch events.
                    <br />
                    You can add several events
                    <br />
                    <Box width="520px" my={1}>
                      <DJSimpleTextBlock
                        codePrimary={`# event 1
new BranchUniversalObject()...  
new BranchEvent()...
logEvent()

# event 2
new BranchUniversalObject()...  
new BranchEvent()...
logEvent()
...`}
                        showLineNumbers={false}
                      />
                    </Box>
                  </Note>
                </>
              ) : (
                <Note variant="tip" title="" sx={{ width: "600px" }}>
                  Please paste below the <code>onStart</code> code
                  from your <code>MainActivity</code>.
                </Note>
              )}

              <TextField
                id="multiline3"
                // label="Original code"
                variant="outlined"
                placeholder="Paste your Branch SDK code here"
                multiline={true}
                value={inputCode}
                onChange={(e) => {
                  setInputCode(e.target.value);
                }}
                minRows={10}
                maxRows={30}
                size="xl"
              />
              <Button
                onClick={migrateCode}
                color={"primary"}
                variant={"contained"}
              >
                Convert
              </Button>
            </Box>
          )}
          {isConverted && !isLoading && (
            <>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                mt={1}
                mb={2}
              >
                <Box display="flex" alignItems="center">
                  <Typography variant="body2">AppsFlyer Only</Typography>
                  <Switch
                    checked={viewMode === "split"}
                    onChange={(e) =>
                      setViewMode(e.target.checked ? "split" : "single")
                    }
                    inputProps={{
                      "aria-label": "Toggle View Mode",
                    }}
                    sx={{marginLeft: 4, marginRight: 1}}
                  />
                  <Typography variant="body2">Branch → AppsFlyer</Typography>
                </Box>

                {viewMode === "single" ? (
                  <Box position="relative" mt={2} maxWidth="700px" width="100%">
                    <Box
                      position="absolute"
                      top="4px"
                      right="4px"
                      style={{ color: "white" }}
                    >
                      <Tooltip placement="top" title="Copy">
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label="Copy"
                          onClick={() => copyToClipboard(newCode)}
                          style={{ padding: "3px", border: "1px solid" }}
                        >
                          <ContentCopyRoundedIcon
                            fontSize="small"
                            style={{ cursor: "pointer", fontSize: "14px" }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <DJSimpleTextBlock
                      codePrimary={newCode}
                      showLineNumbers={true}
                      language="java"
                    />
                  </Box>
                ) : (
                  <Box display="flex" gap={2} mt={2} flexWrap="wrap">
                    {/* Old Code */}
                    <Box position="relative" width="100%" maxWidth="550px" flex={1} minWidth="300px">
                      <DJSimpleTextBlock
                        codePrimary={inputCode}
                        showLineNumbers={true}
                        language="java"
                      />
                    </Box>

                    {/* New Code */}
                    <Box position="relative" width="100%" maxWidth="550px" flex={1} minWidth="300px">
                      <Box
                        position="absolute"
                        top="4px"
                        right="4px"
                        style={{ color: "white" }}
                      >
                        <Tooltip placement="top" title="Copy">
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label="Copy"
                            onClick={() => copyToClipboard(newCode)}
                            style={{ padding: "3px", border: "1px solid" }}
                          >
                            <ContentCopyRoundedIcon
                              fontSize="small"
                              style={{ cursor: "pointer", fontSize: "14px" }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <DJSimpleTextBlock
                        codePrimary={newCode}
                        showLineNumbers={true}
                        language="java"
                        wrapLongLines={false}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Box
        display={"flex"}
        width={"100%"}
        justifyContent="space-between"
        alignSelf="end"
        justifySelf="end"
        textAlign="end"
      >
        <Button
          variant="outlined"
          size="medium"
          color="secondary"
          style={{ marginRight: "5px" }}
          onClick={() => {
            if (isConverted) {
              retry();
            } else {
              navigate(-1);
            }
          }}
        >
          {t("general_Back")}
        </Button>
        {isConverted && !isLoading && (
          <Button
            color={"primary"}
            variant={"contained"}
            onClick={() => proceedToTest()}
          >
            Procced to test
          </Button>
        )}
      </Box>
    </Container>
  );
};

export default transition(SdkMigration);
